import React, { useState } from 'react';
import { Lightbulb, BookOpen, TrendingUp, Award, Zap } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';

const EXAM_TIPS = {
  general: [
    {
      title: 'Read Questions Carefully',
      tip: 'Read each question at least twice before answering. Underline key words.',
      icon: '📖'
    },
    {
      title: 'Time Management',
      tip: 'Allocate time per question: 1M = 1min, 2M = 2-3min, 4M = 5-6min, 8M = 10-12min',
      icon: '⏰'
    },
    {
      title: 'Answer Easy Questions First',
      tip: 'Build confidence by solving easier questions first, then tackle difficult ones.',
      icon: '✅'
    },
    {
      title: 'Write Point-wise',
      tip: 'Use numbered points (1, 2, 3...) for better presentation and easy checking.',
      icon: '📝'
    },
    {
      title: 'Highlight Keywords',
      tip: 'Underline or bold important terms and formulas to catch examiner\'s attention.',
      icon: '✨'
    },
    {
      title: 'Neat Handwriting',
      tip: 'Write neatly with proper spacing. Neat presentation can earn extra marks.',
      icon: '✍️'
    }
  ],
  mathematics: [
    {
      title: 'Show All Steps',
      tip: 'Write all calculation steps clearly. Partial marks are given for correct method.',
      icon: '➗'
    },
    {
      title: 'Box Final Answers',
      tip: 'Put a box around your final answer to make it clearly visible.',
      icon: '📦'
    },
    {
      title: 'Draw Diagrams',
      tip: 'Always draw neat diagrams for geometry questions. Use pencil and scale.',
      icon: '📐'
    },
    {
      title: 'Check Units',
      tip: 'Don\'t forget to write units (cm, m, kg, etc.) in your final answer.',
      icon: '📏'
    }
  ],
  science: [
    {
      title: 'Draw Labeled Diagrams',
      tip: 'Draw clear diagrams with proper labels using pencil. Diagrams carry marks.',
      icon: '🔬'
    },
    {
      title: 'Chemical Equations',
      tip: 'Always balance chemical equations and write state symbols (s, l, g, aq).',
      icon: '⚗️'
    },
    {
      title: 'Use Scientific Terms',
      tip: 'Use exact scientific terms instead of common words (e.g., "respiration" not "breathing").',
      icon: '🧪'
    }
  ],
  languages: [
    {
      title: 'Paragraph Structure',
      tip: 'Start with introduction, develop in middle paragraphs, end with conclusion.',
      icon: '📄'
    },
    {
      title: 'Grammar Check',
      tip: 'Review tenses, subject-verb agreement, and spellings before submitting.',
      icon: '✓'
    },
    {
      title: 'Quote Examples',
      tip: 'Support your answers with examples from lessons or real life.',
      icon: '💬'
    }
  ],
  lastDay: [
    {
      title: 'Revise Formula Sheet',
      tip: 'Create and revise a formula sheet for Math, Physics, and Chemistry.',
      icon: '📋'
    },
    {
      title: 'Sleep Well',
      tip: 'Get 7-8 hours of sleep before exam day. Don\'t study late night.',
      icon: '😴'
    },
    {
      title: 'Keep Calm',
      tip: 'Stay calm and confident. Take deep breaths if you feel nervous.',
      icon: '🧘'
    }
  ]
};

export default function QuickTips() {
  const [selectedCategory, setSelectedCategory] = useState('general');

  const categories = [
    { id: 'general', label: 'General Tips', icon: Lightbulb },
    { id: 'mathematics', label: 'Mathematics', icon: TrendingUp },
    { id: 'science', label: 'Science', icon: BookOpen },
    { id: 'languages', label: 'Languages', icon: Award },
    { id: 'lastDay', label: 'Last Day Tips', icon: Zap }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div className="bg-white rounded-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Quick Tips & Exam Tricks</h1>
          <p className="text-slate-600">Expert tips to score maximum marks in SSC board exams</p>
        </div>

        {/* Category Tabs */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-900 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                  data-testid={`tips-category-${category.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tips Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {EXAM_TIPS[selectedCategory].map((tip, index) => (
            <div
              key={index}
              className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-lg transition-shadow"
              data-testid={`tip-card-${index}`}
            >
              <div className="flex items-start space-x-4">
                <div className="text-4xl">{tip.icon}</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{tip.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{tip.tip}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Success Formula */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-8">
          <h2 className="text-2xl font-bold text-green-900 mb-4">🎯 Success Formula</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-900">70%</p>
              <p className="text-sm text-slate-600 mt-1">Regular Study</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-900">20%</p>
              <p className="text-sm text-slate-600 mt-1">Practice Tests</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-900">5%</p>
              <p className="text-sm text-slate-600 mt-1">Revision</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-900">5%</p>
              <p className="text-sm text-slate-600 mt-1">Exam Strategy</p>
            </div>
          </div>
        </div>

        {/* Motivation */}
        <div className="bg-blue-900 rounded-lg p-8 text-center">
          <p className="text-2xl font-bold text-white mb-2">
            "Success is the sum of small efforts repeated day in and day out"
          </p>
          <p className="text-blue-200">Keep practicing with TopMarks! 🎓</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
